import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { stripMarkdownFormatting } from 'lib/string/stripMarkdown'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { getLunarNewYearRegionAmounts } from 'takeovers/lunarNewYearTakeover/lunarNewYearDetails'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

const TakeoverHeader = styled.div`
  position: relative;
  height: ${rem(130)};
  display: flex;
  align-items: center;
  padding: 0 ${rem(20)};
`

const TakeoverBackground = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
`

const HeaderContent = styled(Group)`
  position: relative;
  z-index: 1;
`

function LunarSiteTakeoverDetailsModal() {
  const { currentRegionCode } = useContext(GeoContext)
  const intl = useIntl()
  const regionAmounts = getLunarNewYearRegionAmounts(currentRegionCode)
  const siteTakeoverTheme = useSiteTakeoverTheme()
  const modalElement = useModalElementContext()

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const logoConfig = useMemo<App.SiteTakeoverImageConfig | undefined>(() => {
    const fallbackLogoRef = siteTakeoverTheme?.logoRef
    return fallbackLogoRef ? { id: fallbackLogoRef } : undefined
  }, [siteTakeoverTheme?.logoRef])

  const title = siteTakeoverBannerContentPayload?.title
  const subtitle = siteTakeoverBannerContentPayload?.subtitle ? stripMarkdownFormatting(siteTakeoverBannerContentPayload.subtitle) : undefined
  const backgroundStyle = siteTakeoverBannerContentPayload?.backgroundStyle
  const ctaLink = siteTakeoverBannerContentPayload?.ctaLink

  const shouldShowHeader = !!(backgroundStyle && logoConfig && title && subtitle)

  const viewAllOffersLabel = intl.formatMessage({
    id: 'ktSqB5nFDb',
    defaultMessage: 'View all offers',
    description: 'CTA button text in Lunar New Year details modal',
  })

  const closeLabel = intl.formatMessage({
    id: 'A41hXyMzxe',
    defaultMessage: 'Close',
    description: 'Close button text in Lunar New Year details modal',
  })

  const handleClose = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  return (
    <ModalBase>
      <ModalBody>
        {shouldShowHeader && <TakeoverHeader>
          <TakeoverBackground backgroundStyle={backgroundStyle} />
          <HeaderContent direction="horizontal" gap={12} verticalAlign="center">
            <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={48} height={48} />
            <Group direction="vertical" gap={4}>
              <Heading variant="heading5" colour="neutral-eight">{stripMarkdownFormatting(title)}</Heading>
              <BodyText variant="medium" colour="neutral-eight">{subtitle}</BodyText>
            </Group>
          </HeaderContent>
        </TakeoverHeader>}
        <ModalContent>
          <Group direction="vertical" gap={16}>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'LuJ1oh5+vJ',
                defaultMessage: 'Discover the largest selection of Lunar New Year exclusive offers for top destinations worldwide!',
                description: 'Introductory text in the Lunar New Year details modal',
              })}
            </BodyText>
            {regionAmounts && <div>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'vAn8M77vpU',
                  defaultMessage: 'How does it work?',
                  description: 'Section heading for Lunar New Year loyalty benefits explanation',
                })}
              </BodyText>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'n/sDWA+0Ya',
                  defaultMessage: 'This Lunar New Year, fast-track your loyalty status and unlock more VIP perks. Spend {silverAmount} to instantly unlock SOCIÉTÉ Silver, and {goldAmount} to instantly unlock SOCIÉTÉ Gold',
                  description: 'Region-specific Lunar New Year loyalty tier spend requirements',
                }, { silverAmount: intl.formatMessage(regionAmounts.silverAmount), goldAmount: intl.formatMessage(regionAmounts.goldAmount) })}
              </BodyText>
            </div>}
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        actionsLayout="stacked"
        tabletActionsLayout="auto"
        primaryActionProps={ctaLink ? { children: viewAllOffersLabel, to: ctaLink } : undefined}
        secondaryActionProps={{ children: closeLabel, onClick: handleClose }}
      />
    </ModalBase>
  )
}

export default LunarSiteTakeoverDetailsModal
